"use client"

export const dynamic = 'force-dynamic'

import React, { useEffect, useState, useMemo } from "react"
import { AlertTriangle, Clock, Mail, Megaphone, MessageSquare, MousePointerClick, Send, Target, Users, DollarSign } from "lucide-react"
import { AuthGuard } from "@/components/auth-guard"
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar"
import { AppSidebar } from "@/components/app-sidebar"
import { DashboardHeader } from "@/components/dashboard-header"
import { StatsCard } from "@/components/stats-card"
import { CampaignTable } from "@/components/campaign-table"
import { PerformanceChart } from "@/components/performance-chart"
import { PlatformBreakdown } from "@/components/platform-breakdown"
import {
  getCampaigns,
  getDashboardContactsSummary,
  getDashboardActivitySummary,
  getMessagingSummary,
  getRecentMessagingActivity,
  type ActivityLogItem,
  type Campaign,
  type MessagingSummary,
} from "@/lib/marketing-api"
import { useClientContext } from "@/lib/client-context"
import { useAuth } from "@/lib/auth-context"

export default function DashboardPage() {
  const { user } = useAuth()
  const { activeClient } = useClientContext()
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [messagingSummary, setMessagingSummary] = useState<MessagingSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [recentMessagingActivity, setRecentMessagingActivity] = useState<Array<ActivityLogItem & { id?: string }>>([])
  const [messagingError, setMessagingError] = useState(false)
  const [contactStats, setContactStats] = useState<any>(null)
  const [activitySeries, setActivitySeries] = useState<any[]>([])
  const [messagingLoading, setMessagingLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    setMessagingLoading(true)
    setMessagingError(false)
    const clientId = activeClient?._id
    Promise.all([
      getCampaigns({ limit: 500, clientId }),
      getMessagingSummary(clientId ? { clientId } : undefined).catch(() => {
        setMessagingError(true)
        return null as any
      }),
      getRecentMessagingActivity(clientId ? { clientId, limit: 10 } : { limit: 10 }).catch(() => {
        setMessagingError(true)
        return null as any
      }),
      clientId ? getDashboardContactsSummary({ clientId }) : Promise.resolve(null as any),
      clientId ? getDashboardActivitySummary({ clientId, days: 30 }) : Promise.resolve(null as any),
    ])
      .then(([campaignRes, messagingRes, recentActivityRes, contactRes, activityRes]) => {
        setCampaigns(campaignRes?.data?.campaigns || [])
        setMessagingSummary(messagingRes?.data || null)
        setRecentMessagingActivity(recentActivityRes?.data || [])
        setContactStats(contactRes?.data || null)
        setActivitySeries(activityRes?.data || [])
      })
      .catch(() => {
        setCampaigns([])
        setMessagingSummary(null)
        setRecentMessagingActivity([])
        setContactStats(null)
        setActivitySeries([])
      })
      .finally(() => {
        setLoading(false)
        setMessagingLoading(false)
      })
  }, [activeClient?._id])

  const stats = useMemo(() => {
    const active = campaigns.filter((c) => c.status === "active").length
    const totalLeads = campaigns.reduce((s, c) => s + (c.leads || 0), 0)
    const totalSpent = campaigns.reduce((s, c) => s + (c.spent || 0), 0)
    const avgCpl = totalLeads > 0 ? totalSpent / totalLeads : 0
    return { active, totalLeads, totalSpent, avgCpl }
  }, [campaigns])

  const messagingPerformance = useMemo(() => {
    const email = messagingSummary?.email
    const sms = messagingSummary?.sms
    const emailSentBase = email ? email.sent + email.delivered + email.opened + email.clicked + email.bounced + email.failed + email.unsubscribed : 0
    const emailDeliveredBase = email ? email.delivered + email.opened + email.clicked : 0
    const smsSentBase = sms ? sms.sent + sms.delivered + sms.failed : 0

    return {
      emailSent: emailSentBase,
      emailDelivered: emailDeliveredBase,
      emailOpened: email?.opened || 0,
      emailClicked: email?.clicked || 0,
      smsSent: smsSentBase,
      smsDelivered: sms?.delivered || 0,
      failures: (email?.failed || 0) + (email?.bounced || 0) + (sms?.failed || 0),
      deliveryRate: messagingSummary?.combined.sent ? (messagingSummary.combined.delivered / messagingSummary.combined.sent) * 100 : 0,
    }
  }, [messagingSummary])

  const performanceData = useMemo(() => {
    const byMonth: Record<string, { impressions: number; clicks: number; leads: number }> = {}
    campaigns.forEach((c) => {
      const d = new Date(c.startDate || c.createdAt)
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
      if (!byMonth[key]) byMonth[key] = { impressions: 0, clicks: 0, leads: 0 }
      byMonth[key].impressions += c.impressions || 0
      byMonth[key].clicks += c.clicks || 0
      byMonth[key].leads += c.leads || 0
    })
    return Object.entries(byMonth)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, val]) => {
        const [y, m] = key.split("-")
        const monthName = new Date(Number(y), Number(m) - 1).toLocaleString("en", { month: "short", year: "2-digit" })
        return { date: monthName, ...val }
      })
  }, [campaigns])

  const platformData = useMemo(() => {
    const byPlatform: Record<string, { spend: number; budget: number; leads: number }> = {}
    campaigns.forEach((c) => {
      const platforms = c.platforms?.length ? c.platforms : ["Other"]
      const perPlat = platforms.length
      platforms.forEach((p) => {
        const name = p.charAt(0).toUpperCase() + p.slice(1)
        if (!byPlatform[name]) byPlatform[name] = { spend: 0, budget: 0, leads: 0 }
        byPlatform[name].spend += (c.spent || 0) / perPlat
        byPlatform[name].budget += (c.budget || c.spendCap || 0) / perPlat
        byPlatform[name].leads += (c.leads || 0) / perPlat
      })
    })
    const colors = ["bg-chart-4", "bg-chart-2", "bg-chart-5", "bg-chart-3", "bg-chart-1"]
    return Object.entries(byPlatform).map(([name, val], i) => ({
      name,
      spend: Math.round(val.spend),
      totalBudget: Math.round(val.budget) || Math.round(val.spend * 1.2),
      leads: Math.round(val.leads),
      color: colors[i % colors.length],
    }))
  }, [campaigns])

  const chartData = useMemo(() => {
    const hasActivity = activitySeries?.some((row: any) => (row.email || 0) > 0 || (row.sms || 0) > 0)
    if (hasActivity) {
      return activitySeries.map((row: any) => ({
        date: new Date(row.date).toLocaleDateString("en", { month: "short", day: "numeric" }),
        impressions: row.email || 0,
        clicks: row.sms || 0,
        leads: (row.email || 0) + (row.sms || 0),
      }))
    }
    return performanceData
  }, [activitySeries, performanceData])


  return (
    <AuthGuard>
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <DashboardHeader
          title={`Welcome back${user?.firstname ? `, ${user.firstname}` : ''}`}
          description="Campaign overview with real-time data."
        />

        <main className="flex-1 p-4 md:p-6 space-y-6">
          {/* Stats Grid */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <StatsCard
              title="Active Campaigns"
              value={loading ? "..." : String(stats.active)}
              icon={Megaphone}
            />
            <StatsCard
              title="Active Contacts"
              value={loading ? "..." : (contactStats?.total ?? stats.totalLeads).toLocaleString()}
              icon={Users}
            />
            <StatsCard
              title="Total Spend"
              value={loading ? "..." : `$${stats.totalSpent.toLocaleString()}`}
              icon={DollarSign}
            />
            <StatsCard
              title="Delivery Rate"
              value={loading ? "..." : `${Number(messagingPerformance.deliveryRate || 0).toFixed(1)}%`}
              icon={Target}
            />
          </div>

          {/* Messaging Performance */}
          <section className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Messaging Performance</h2>
                {messagingError && (
                  <p className="text-sm text-destructive">Unable to load messaging metrics.</p>
                )}
                {!messagingError && loading && (
                  <p className="text-sm text-muted-foreground">Loading messaging metrics...</p>
                )}
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-7">
              <StatsCard title="Email Sent" value={loading ? "..." : messagingPerformance.emailSent.toLocaleString()} icon={Send} />
              <StatsCard title="Email Delivered" value={loading ? "..." : messagingPerformance.emailDelivered.toLocaleString()} icon={Mail} />
              <StatsCard title="Email Opens" value={loading ? "..." : messagingPerformance.emailOpened.toLocaleString()} icon={Users} />
              <StatsCard title="Email Clicks" value={loading ? "..." : messagingPerformance.emailClicked.toLocaleString()} icon={MousePointerClick} />
              <StatsCard title="SMS Sent" value={loading ? "..." : messagingPerformance.smsSent.toLocaleString()} icon={MessageSquare} />
              <StatsCard title="SMS Delivered" value={loading ? "..." : messagingPerformance.smsDelivered.toLocaleString()} icon={Target} />
              <StatsCard title="Failures" value={loading ? "..." : messagingPerformance.failures.toLocaleString()} icon={AlertTriangle} />
            </div>
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-card-foreground">Recent Messaging Activity</h3>
              </div>
              {loading ? (
                <p className="text-sm text-muted-foreground">Loading recent activity...</p>
              ) : recentMessagingActivity.length === 0 ? (
                <p className="text-sm text-muted-foreground">No recent messaging activity yet.</p>
              ) : (
                <div className="divide-y divide-border">
                  {recentMessagingActivity.slice(0, 10).map((activity) => (
                    <div key={activity.id || activity._id} className="flex items-center justify-between gap-3 py-2 text-sm">
                      <div className="min-w-0">
                        <p className="truncate font-medium text-card-foreground">{formatActivityType(activity.activityType)}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          {activity.channel} · {activity.referenceId || getActivityContactId(activity) || "No reference"}
                        </p>
                      </div>
                      <span className="shrink-0 text-xs text-muted-foreground">{formatActivityDate(activity.occurredAt || activity.createdAt)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Charts Row */}
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <PerformanceChart
                data={chartData}
                loading={loading}
              />
            </div>
            <PlatformBreakdown data={platformData} loading={loading} />
          </div>

          <SmsDeliverabilityPanel summary={messagingSummary} loading={messagingLoading} />

          {/* Campaigns Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">Active Campaigns</h2>
              <a
                href="/campaigns"
                className="text-sm text-primary hover:text-primary/80 transition-colors"
              >
                View all campaigns
              </a>
            </div>
            <CampaignTable />
          </div>

          {/* Activity Section */}
          <div className="grid gap-6 lg:grid-cols-2">
            {/* <MessageActivityPanel clientId={activeClient?._id} /> */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-foreground">Quick Actions</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <QuickActionCard
                  title="Create Campaign"
                  description="Launch a new multi-platform campaign"
                  icon={Megaphone}
                  href="/campaigns/create"
                />
                <QuickActionCard
                  title="Upload Creatives"
                  description="Add new images, videos, or text"
                  icon={Target}
                  href="/media"
                />
                <QuickActionCard
                  title="View Leads"
                  description="Check your latest lead submissions"
                  icon={Users}
                  href="/leads"
                />
                <QuickActionCard
                  title="Analytics Report"
                  description="Deep dive into performance data"
                  icon={DollarSign}
                  href="/analytics"
                />
              </div>
            </div>
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
    </AuthGuard>
  )
}

function SmsDeliverabilityPanel({
  summary,
  loading,
}: {
  summary: MessagingSummary | null
  loading: boolean
}) {
  const sms = summary?.sms
  const topErrors = sms?.topTwilioErrorCodes || sms?.twilioErrorCodes || []

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-primary" />
          <h2 className="text-lg font-semibold text-foreground">SMS Deliverability</h2>
        </div>
        <span className="text-sm text-muted-foreground">
          {loading ? "Loading..." : `${sms?.total || 0} tracked SMS`}
        </span>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-4">
        <MetricTile label="Delivered" value={loading ? "..." : `${sms?.deliveryRate || 0}%`} />
        <MetricTile label="Failed" value={loading ? "..." : `${sms?.failureRate || 0}%`} />
        <MetricTile label="Opt-out Rate" value={loading ? "..." : `${sms?.optOutRate || 0}%`} />
        <MetricTile label="Opt-outs" value={loading ? "..." : String(sms?.optedOut || 0)} />
      </div>

      <div className="mt-4">
        <div className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground">
          <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          Top Twilio Error Codes
        </div>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading error diagnostics...</p>
        ) : topErrors.length === 0 ? (
          <p className="text-sm text-muted-foreground">No SMS failure codes recorded.</p>
        ) : (
          <div className="grid gap-2 md:grid-cols-3">
            {topErrors.slice(0, 3).map((error) => (
              <div key={error.code} className="rounded-md border border-border bg-background p-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-mono text-sm font-semibold text-foreground">{error.code}</span>
                  <span className="text-sm text-muted-foreground">{error.count}</span>
                </div>
                {error.message ? (
                  <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{error.message}</p>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}

function MetricTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-background p-3">
      <p className="text-xs font-medium uppercase text-muted-foreground">{label}</p>
      <p className="mt-1 text-xl font-semibold text-foreground">{value}</p>
    </div>
  )
}

function QuickActionCard({
  title,
  description,
  icon: Icon,
  href,
}: {
  title: string
  description: string
  icon: React.ElementType
  href: string
}) {
  return (
    <a
      href={href}
      className="group flex flex-col gap-2 rounded-lg border border-border bg-card p-4 transition-all hover:border-primary/50 hover:bg-secondary/50"
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
        <Icon className="h-5 w-5 text-primary" />
      </div>
      <div>
        <h3 className="font-medium text-card-foreground group-hover:text-primary transition-colors">
          {title}
        </h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </a>
  )
}

function formatActivityType(type: string) {
  return type.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
}

function getActivityContactId(activity: ActivityLogItem) {
  if (!activity.contactId) return ""
  if (typeof activity.contactId === "string") return activity.contactId
  return activity.contactId._id || activity.contactId.email || activity.contactId.phone || ""
}

function formatActivityDate(value?: string) {
  if (!value) return ""
  return new Date(value).toLocaleString("en", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}
